// ─────────────────────────────────────────────────────────────────────────
// COMPUTE QUEUE PANEL  (Layer 3 of the queue reconciler)
//
// Read-only visibility into what is actually queued / in-flight on the Celery
// pipeline queues, mapped back to symbol / opt / phase, with orphaned messages
// flagged. Superusers additionally get a Reconcile action that previews
// (dry-run) then revokes + purges orphaned messages from the broker.
//
// Why this exists: the DB (OptimizationResult.*_status) and the Redis broker
// (ready lists + unacked store) drift apart when a spot worker dies mid-job or
// a run is abandoned. This panel lets the user SEE the queue instead of
// guessing why a run isn't progressing — and lets an admin clean up zombies.
//
// Mobile-first, dark-default, institutional styling (gold accent, uppercase
// labels). Collapsible. Auto-polls every 8s while expanded.
// ─────────────────────────────────────────────────────────────────────────
import React, { useCallback, useEffect, useState } from 'react';
import { tradingService } from '../../../services/tradingService';
import { useAuth } from '../../../context/AuthContext';

const PHASE_COLOR = {
  IS:           'text-blue-600 dark:text-blue-400',
  OOS:          'text-purple-600 dark:text-purple-400',
  NSR:          'text-emerald-600 dark:text-emerald-400',
  pipeline:     'text-amber-600 dark:text-amber-400',
  leaderboards: 'text-cyan-600 dark:text-cyan-400',
  other:        'text-gray-500 dark:text-gray-400',
};

const Spinner = ({ className = 'w-3.5 h-3.5' }) => (
  <svg className={`animate-spin ${className}`} viewBox="0 0 24 24" fill="none">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
  </svg>
);

const Chevron = ({ open }) => (
  <svg className={`w-4 h-4 transition-transform ${open ? 'rotate-90' : ''}`} viewBox="0 0 20 20" fill="currentColor">
    <path fillRule="evenodd" d="M7.21 14.77a.75.75 0 01.02-1.06L11.168 10 7.23 6.29a.75.75 0 111.04-1.08l4.5 4.25a.75.75 0 010 1.08l-4.5 4.25a.75.75 0 01-1.06-.02z" clipRule="evenodd" />
  </svg>
);

export default function ComputeQueuePanel({ showToast }) {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;

  const [open, setOpen]       = useState(false);
  const [snap, setSnap]       = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);
  const [reconciling, setReconciling] = useState(false);
  const [preview, setPreview] = useState(null); // dry-run plan awaiting confirm
  const [purgeTarget, setPurgeTarget] = useState(null); // single item awaiting withdraw confirm
  const [purging, setPurging] = useState(false);

  const fetchQueue = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await tradingService.getComputeQueue();
      setSnap(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on first expand, then poll every 8s while open.
  useEffect(() => {
    if (!open) return;
    fetchQueue();
    const id = setInterval(fetchQueue, 8000);
    return () => clearInterval(id);
  }, [open, fetchQueue]);

  const items       = snap?.items || [];
  const orphanCount = snap?.orphan_count || 0;
  const totalDepth  = snap
    ? Object.values(snap.queues || {}).reduce((a, v) => a + (typeof v === 'number' ? v : 0), 0)
    : 0;
  const workers     = snap?.workers;

  // Reconcile: first dry-run to preview, then user confirms to execute.
  const runPreview = async () => {
    setReconciling(true);
    try {
      const res = await tradingService.reconcileComputeQueue(true);
      setPreview(res.data);
    } catch (e) {
      showToast?.(e.response?.data?.error || 'Reconcile preview failed');
    } finally {
      setReconciling(false);
    }
  };

  const executeReconcile = async () => {
    setReconciling(true);
    try {
      const res = await tradingService.reconcileComputeQueue(false);
      const p = res.data;
      showToast?.(`Reconciled — revoked ${p.revoked}, purged ${p.purged_ready} queued message(s)`);
      setPreview(null);
      fetchQueue();
    } catch (e) {
      showToast?.(e.response?.data?.error || 'Reconcile failed');
    } finally {
      setReconciling(false);
    }
  };

  // Withdraw a single queued message by task_id (revoke + purge from broker).
  // Used to kill a stale or duplicate message even when its run isn't orphaned.
  const confirmPurge = async () => {
    if (!purgeTarget?.task_id) return;
    setPurging(true);
    try {
      const res = await tradingService.purgeComputeQueueItems([purgeTarget.task_id], false);
      const p = res.data;
      const settled = (p.settled || [])
        .map(s => `${s.phase} → failed`)
        .join(', ');
      showToast?.(
        `Withdrawn — purged ${p.purged_ready + p.purged_unacked} message(s)`
        + (settled ? ` · ${settled}` : '')
      );
      setPurgeTarget(null);
      fetchQueue();
    } catch (e) {
      showToast?.(e.response?.data?.error || 'Withdraw failed');
    } finally {
      setPurging(false);
    }
  };

  return (
    <div className="border-b border-gray-200 dark:border-gray-800 bg-gray-50/60 dark:bg-gray-800/20">
      {/* Header / toggle */}
      <button
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center gap-2 px-3 md:px-5 py-2.5 text-left hover:bg-gray-100/60 dark:hover:bg-gray-800/40"
      >
        <span className="text-gray-400 dark:text-gray-500"><Chevron open={open} /></span>
        <span className="text-[10px] uppercase tracking-wider text-gray-500 dark:text-gray-400">
          Compute Queue
        </span>
        {/* Compact status summary, always visible */}
        <span className="ml-1 flex items-center gap-2 text-[11px]">
          <span className="text-gray-600 dark:text-gray-300">{totalDepth} queued</span>
          {orphanCount > 0 && (
            <span className="px-1.5 py-0.5 rounded bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300 font-medium">
              {orphanCount} orphaned
            </span>
          )}
          <span className={workers > 0 ? 'text-emerald-600 dark:text-emerald-400' : 'text-gray-500'}>
            {workers == null ? 'worker: n/a' : `${workers} worker${workers === 1 ? '' : 's'}`}
          </span>
        </span>
        {open && loading && <span className="text-gray-400 ml-auto"><Spinner /></span>}
      </button>

      {open && (
        <div className="px-3 md:px-5 pb-4">
          {error && (
            <div className="mb-3 px-3 py-2 text-xs text-red-700 dark:text-red-300 bg-red-50 dark:bg-red-900/20 rounded">
              {error}
            </div>
          )}

          {/* Queue depths */}
          <div className="flex flex-wrap gap-2 mb-3">
            {snap && Object.entries(snap.queues || {}).map(([q, d]) => (
              <div key={q} className="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 bg-white dark:bg-[#161b22] text-[10px]">
                <span className="text-gray-500 dark:text-gray-400">{q}</span>
                <span className="ml-1.5 font-semibold text-gray-800 dark:text-gray-200">{String(d)}</span>
              </div>
            ))}
          </div>

          {/* Items table */}
          {items.length === 0 ? (
            <div className="text-center py-6 text-xs text-gray-500 dark:text-gray-400">
              {snap ? 'Queue is empty — nothing in flight.' : 'Loading…'}
            </div>
          ) : (
            <div className="overflow-x-auto scrollbar-thin">
              <table className="w-full text-[11px] min-w-[560px]">
                <thead>
                  <tr className="text-[10px] uppercase tracking-wider text-gray-400 dark:text-gray-500 border-b border-gray-200 dark:border-gray-800">
                    <th className="text-left py-1.5 pr-2">Symbol</th>
                    <th className="text-left py-1.5 pr-2">Opt</th>
                    <th className="text-left py-1.5 pr-2">Phase</th>
                    <th className="text-left py-1.5 pr-2">Task</th>
                    <th className="text-left py-1.5 pr-2">State</th>
                    <th className="text-left py-1.5 pr-2">Status</th>
                    <th className="text-left py-1.5 pr-2">Flag</th>
                    {isAdmin && <th className="text-right py-1.5">Action</th>}
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr
                      key={`${it.task_id || i}`}
                      className={`border-b border-gray-100 dark:border-gray-800/60 ${it.orphaned ? 'bg-red-50/60 dark:bg-red-900/10' : ''}`}
                    >
                      <td className="py-1.5 pr-2 font-medium text-gray-800 dark:text-gray-200">{it.symbol || '—'}</td>
                      <td className="py-1.5 pr-2 text-gray-500 dark:text-gray-400">{it.opt_id != null ? `#${it.opt_id}` : '—'}</td>
                      <td className={`py-1.5 pr-2 font-medium ${PHASE_COLOR[it.phase] || PHASE_COLOR.other}`}>{it.phase}</td>
                      <td className="py-1.5 pr-2 text-gray-500 dark:text-gray-400 font-mono text-[10px]">{it.task}</td>
                      <td className="py-1.5 pr-2">
                        <span className={it.state === 'unacked'
                          ? 'text-amber-600 dark:text-amber-400'
                          : 'text-gray-500 dark:text-gray-400'}>
                          {it.state === 'unacked' ? 'in-flight' : 'queued'}
                        </span>
                      </td>
                      <td className="py-1.5 pr-2 text-gray-500 dark:text-gray-400">{it.opt_phase_status || '—'}</td>
                      <td className="py-1.5 pr-2">
                        {it.orphaned
                          ? <span className="text-red-600 dark:text-red-400" title={it.orphan_reason}>orphan</span>
                          : <span className="text-emerald-600 dark:text-emerald-400">ok</span>}
                      </td>
                      {isAdmin && (
                        <td className="py-1.5 text-right">
                          {it.task_id ? (
                            <button
                              onClick={() => setPurgeTarget(it)}
                              disabled={purging}
                              title="Withdraw this message from the broker (revoke + purge)"
                              className="px-1.5 py-0.5 text-[10px] rounded border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-red-500 hover:text-red-600 dark:hover:text-red-400 disabled:opacity-50"
                            >
                              Withdraw
                            </button>
                          ) : <span className="text-gray-400">—</span>}
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Actions */}
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <button
              onClick={fetchQueue}
              disabled={loading}
              className="px-2.5 py-1 text-[11px] rounded border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300 hover:border-brand-gold disabled:opacity-50"
            >
              Refresh
            </button>
            {isAdmin && orphanCount > 0 && !preview && (
              <button
                onClick={runPreview}
                disabled={reconciling}
                className="px-2.5 py-1 text-[11px] rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 flex items-center gap-1.5"
              >
                {reconciling && <Spinner />}
                Reconcile {orphanCount} orphan{orphanCount === 1 ? '' : 's'}
              </button>
            )}
            {!isAdmin && orphanCount > 0 && (
              <span className="text-[10px] text-gray-400 dark:text-gray-500">
                Ask an admin to reconcile orphaned tasks.
              </span>
            )}
          </div>

          {/* Reconcile confirm (dry-run preview → execute) */}
          {preview && (
            <div className="mt-3 px-3 py-3 rounded border border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-900/20">
              <div className="text-xs text-red-800 dark:text-red-200 mb-2">
                This will revoke <strong>{preview.orphan_count}</strong> orphaned task(s) and purge
                queued message(s) from the broker. In-flight tasks abort cooperatively at their next
                checkpoint. This cannot be undone.
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={executeReconcile}
                  disabled={reconciling}
                  className="px-2.5 py-1 text-[11px] rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 flex items-center gap-1.5"
                >
                  {reconciling && <Spinner />}
                  Confirm reconcile
                </button>
                <button
                  onClick={() => setPreview(null)}
                  disabled={reconciling}
                  className="px-2.5 py-1 text-[11px] rounded border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Per-item withdraw confirm */}
          {purgeTarget && (
            <div className="mt-3 px-3 py-3 rounded border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20">
              <div className="text-xs text-amber-800 dark:text-amber-200 mb-2">
                Withdraw <strong>{purgeTarget.task}</strong>
                {purgeTarget.symbol ? <> for <strong>{purgeTarget.symbol}</strong></> : null}
                {purgeTarget.opt_id != null ? <> (opt #{purgeTarget.opt_id}, {purgeTarget.phase})</> : null}?
                This revokes the task and purges its message from the broker. If it's
                {' '}in-flight it aborts at its next checkpoint. This cannot be undone.
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={confirmPurge}
                  disabled={purging}
                  className="px-2.5 py-1 text-[11px] rounded bg-red-600 hover:bg-red-700 text-white disabled:opacity-50 flex items-center gap-1.5"
                >
                  {purging && <Spinner />}
                  Confirm withdraw
                </button>
                <button
                  onClick={() => setPurgeTarget(null)}
                  disabled={purging}
                  className="px-2.5 py-1 text-[11px] rounded border border-gray-300 dark:border-gray-700 text-gray-600 dark:text-gray-300"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
